/**
 * Predicted pool standings derived from a user's winner picks plus optional
 * tiebreaker rankings.
 *
 * Mirrors the SQL function compute_pool_standings_winner_only() exactly so
 * live UI matches what the server computes when grading.
 *
 * Two ordering modes:
 *
 *   Player mode (default):
 *     1. wins DESC
 *     2. user-supplied rank (poolTiebreaks), within a same-win bucket
 *     3. bracket_team_id (stable fallback — NOT user-facing)
 *
 *   Authoritative mode (`options.authoritative`, used when an admin enters
 *   official results — Option A, owner 2026-05-27):
 *     The four advancers are an explicit "tap who moves on, in finish order"
 *     pick. Tapped teams take positions 1..k in tap order; untapped teams fall
 *     below by record. The admin's taps ARE the standings.
 *
 * Seeding was removed as a tiebreaker entirely (owner 2026-05-27: "seeds are
 * never used for tiebreakers"). Head-to-head was dropped earlier (DUB-138):
 * 3+ same-record buckets either fully resolved or stayed cyclic, confusing
 * players. Any teams with the same record now share one tied bucket and the
 * same "tied" label.
 *
 * Row shape:
 *   pool_position    — 1..N final ranking
 *   bracket_team_id, pool_seed, wins
 *   matches_played   — wins + losses counted from picked matches only
 *   tied_group_size  — count of teams sharing this team's `wins`. >1 means
 *                      separation came from user_rank or pool_seed.
 *   needs_user_rank  — TRUE when tied_group_size > 1 AND no user rank
 *                      covers this team. Drives the tiebreaker prompt.
 *
 * @param {Array<{ id, pool_seed }>} teams
 * @param {Array<{ round, match_number, team_one_id, team_two_id }>} matches
 * @param {Map<string, { bracket_team_id }>} picksByKey
 * @param {Array<string>} userRank  ordered bracket_team_ids — partial OK
 * @param {{ authoritative?: boolean }} [options]
 */
export function computePoolStandings(teams, matches, picksByKey, userRank = [], options = {}) {
  const { authoritative = false } = options
  const stats = new Map()
  for (const t of teams) {
    stats.set(t.id, {
      bracket_team_id: t.id,
      pool_seed: t.pool_seed,
      wins: 0,
      matches_played: 0,
    })
  }

  for (const m of matches) {
    const key = `${m.round}:${m.match_number}`
    const pick = picksByKey.get(key)
    if (!pick?.bracket_team_id) continue
    const winnerId = pick.bracket_team_id
    if (!stats.has(winnerId)) continue
    stats.get(winnerId).wins += 1
    if (stats.has(m.team_one_id)) stats.get(m.team_one_id).matches_played += 1
    if (stats.has(m.team_two_id)) stats.get(m.team_two_id).matches_played += 1
  }

  const userRankPos = new Map()
  userRank.forEach((teamId, idx) => userRankPos.set(teamId, idx + 1))

  const enriched = Array.from(stats.values()).map(s => ({
    ...s,
    user_rank: userRankPos.get(s.bracket_team_id) || null,
  }))

  const groupSize = new Map()
  for (const e of enriched) {
    groupSize.set(e.wins, (groupSize.get(e.wins) || 0) + 1)
  }

  const byId = (a, b) => (a.bracket_team_id < b.bracket_team_id ? -1 : 1)

  if (authoritative) {
    // Explicit "you pick who advances": tapped teams hold positions 1..k in
    // tap order; everyone else falls below by record. No wins-gating, no seed.
    enriched.sort((a, b) => {
      if (a.user_rank && b.user_rank) return a.user_rank - b.user_rank
      if (a.user_rank) return -1
      if (b.user_rank) return 1
      if (b.wins !== a.wins) return b.wins - a.wins
      return byId(a, b)
    })
  } else {
    // Player mode: record first, user-resolved ties within a record bucket,
    // then a stable id fallback (replaces the old pool_seed fallback).
    enriched.sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins
      const aRank = a.user_rank ?? 9999
      const bRank = b.user_rank ?? 9999
      if (aRank !== bRank) return aRank - bRank
      return byId(a, b)
    })
  }

  return enriched.map((e, i) => {
    const tied_group_size = groupSize.get(e.wins) || 1
    const pool_position = i + 1
    return {
      pool_position,
      bracket_team_id: e.bracket_team_id,
      pool_seed: e.pool_seed,
      wins: e.wins,
      matches_played: e.matches_played,
      tied_group_size,
      // In authoritative mode the admin chooses outright, so nothing "needs" a
      // tiebreaker rank.
      needs_user_rank: !authoritative && tied_group_size > 1 && e.user_rank == null,
    }
  })
}

/**
 * Pair the ranked Pool A and Pool B standings into the four Sunday slots.
 */
export function deriveSundayMatchups(poolAStandings, poolBStandings) {
  const out = []
  for (let i = 1; i <= 4; i++) {
    const a = poolAStandings.find(s => s.pool_position === i)
    const b = poolBStandings.find(s => s.pool_position === i)
    if (!a || !b) continue
    out.push({
      sunday_seed_index: i,
      team_a_id: a.bracket_team_id,
      team_b_id: b.bracket_team_id,
    })
  }
  return out
}
